import { Input, Text } from '@viapeople/components'
import { useState } from 'react'
import { useDictation } from '../../hooks/useDictation'
import { DictationControls } from '../dictation/DictationControls'
import type { DictationReviewSnapshot, DictationSessionToken, DictationTone } from '../dictation/dictation.types'
import { FormLabelWithTooltip } from './FormLabelWithTooltip'
import type { ComponentProps, TextFieldCustomProps } from './types'

type FormsTextFieldProps = ComponentProps & {
	value?: string
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
	readOnly?: boolean
	disabled?: boolean
	hasError?: boolean
	dictationEnabled?: boolean
	allowAiRewrite?: boolean
	onRequestDictationToken?: () => Promise<DictationSessionToken | null>
	onRewriteDictatedText?: (input: string, tone: DictationTone) => Promise<string>
	onDictationSnapshot?: (snapshot: DictationReviewSnapshot) => void
}

export function FormsTextField({
	component,
	value,
	onChange,
	readOnly,
	disabled,
	required,
	hasError,
	dictationEnabled = false,
	allowAiRewrite = false,
	onRequestDictationToken,
	onRewriteDictatedText,
	onDictationSnapshot,
}: FormsTextFieldProps) {
	const { label, labelColor, labelSize, labelWeight, placeholder, tooltipEnabled, tooltipText } =
		component.custom as TextFieldCustomProps
	const [internalValue, setInternalValue] = useState('')

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (onChange) {
			onChange(e)
		} else {
			setInternalValue(e.target.value)
		}
	}

	const resolvedValue = value !== undefined ? value : internalValue
	const dictation = useDictation({
		fieldId: component.id,
		value: resolvedValue,
		profile: 'sentence',
		onRequestSessionToken: onRequestDictationToken,
		onSnapshot: onDictationSnapshot,
		onRewrite: onRewriteDictatedText,
		onChangeValue: (next) => {
			const syntheticEvent = {
				target: { value: next },
			} as React.ChangeEvent<HTMLInputElement>
			handleChange(syntheticEvent)
		},
	})

	return (
		<div className='flex flex-col gap-1 w-full'>
			{label && !readOnly && (
				<FormLabelWithTooltip
					label={label}
					labelColor={labelColor}
					labelSize={labelSize}
					labelWeight={labelWeight}
					required={required}
					readOnly={readOnly}
					tooltipEnabled={tooltipEnabled}
					tooltipText={tooltipText}
				/>
			)}
			<div className='flex items-center gap-1'>
				{readOnly ? (
					<div className='bg-neutral-50 border border-neutral-200 rounded-md px-6 py-4'>
						<Text>{value}</Text>
					</div>
				) : (
					<div className='w-full flex flex-col gap-1'>
						<Input
							id={component.id}
							type='text'
							value={resolvedValue}
							placeholder={placeholder}
							onChange={handleChange}
							disabled={disabled}
							variant={hasError ? 'error' : 'default'}
						/>
						{dictationEnabled && (
							<DictationControls
								disabled={disabled}
								allowRewrite={allowAiRewrite}
								isListening={dictation.isListening}
								isProcessing={dictation.state === 'processing'}
								error={dictation.error}
								interimTranscript={dictation.interimTranscript}
								draftTranscript={dictation.draftTranscript}
								onStart={() => void dictation.startDictation()}
								onStop={dictation.stopDictation}
								onInsert={dictation.insertDraft}
								onReplace={dictation.replaceWithDraft}
								canUndoRewrite={dictation.canUndoRewrite}
								onDiscard={dictation.discardDraft}
								onReset={dictation.resetDraft}
								onRewrite={(tone) => void dictation.rewriteText(tone)}
								onUndoRewrite={dictation.undoRewrite}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
